import { useState, forwardRef } from 'react';
import type { ChangeEvent } from 'react';
import clsx from 'clsx';

import type { LinksFunction } from '@remix-run/node';
import type { SharedDropdownProps } from './shared-dropdown.types';
import { SharedIcon } from '../shared-icon/shared-icon';

import styles from './shared-dropdown.css?url';

export const links: LinksFunction = () => {
  return [{ rel: 'stylesheet', href: styles }];
};

export const SharedDropdown = forwardRef<
  HTMLSelectElement,
  SharedDropdownProps
>(
  (
    {
      landingType,
      className = '',
      theme = 'light',
      label,
      error,
      options = [],
      ...props
    },
    ref,
  ) => {
    const [value, setValue] = useState<string>('');

    const classNames = clsx(
      'shared-dropdown',
      `shared-dropdown--${theme}`,
      `shared-dropdown--${landingType}`,
      className,
    );

    const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
      setValue(event.target.value);
      props.onChange?.(event);
    };

    return (
      <div className={classNames}>
        <label className="shared-dropdown__inner">
          {label && <p className="shared-dropdown__label">{label}</p>}
          <div className="shared-dropdown__field-wrapper">
            <select
              ref={ref}
              className={clsx('shared-dropdown__field', {
                'shared-dropdown__field--error': !!error,
                'shared-dropdown__field--placeholder': !value,
              })}
              {...props}
              id={props.name}
              value={value}
              onChange={handleChange}
              aria-invalid={!!error}
            >
              <option value="">{props.placeholder}</option>
              {options.map(({ label, value }) => (
                <option key={label + value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <SharedIcon
              name="chevron-down"
              size={24}
              className="shared-dropdown__chevron"
            />
          </div>
        </label>

        {error && (
          <div className="shared-dropdown__error" aria-live="assertive">
            <SharedIcon
              name="exclamation-circle-empty"
              className="shared-dropdown__error-icon"
            />
            <p>{error}</p>
          </div>
        )}
      </div>
    );
  },
);

SharedDropdown.displayName = 'SharedDropdown';
